import { ATA_PRESENCA_LABEL, TASK_STATUS_LABEL, type AtaPresenca, type TaskStatus } from './enums'
import { daysBetween, weekLabel } from './iso-week'
import { isOpen } from './visibility'

/**
 * Modelo da ATA de Reunião Q3 (modelo "ATA - Q3 - R01" da Flexibase) como
 * dados puros. O PDF (apps/web/src/modules/meeting/ata-pdf.ts) só desenha o
 * que sai daqui; a ATA fechada guarda este objeto em `supply.ata.snapshot`
 * para o PDF ser o mesmo amanhã.
 *
 * Seções: 1 informações · 2 objetivo · 3 Que bom · 4 Que pena · 5 Que tal?
 * (decisões) · 6 prioridades da próxima semana · 7 plano de ação ·
 * 8 pendências · 9 assuntos para acompanhamento.
 */

export const ATA_FOOTER =
  'ATA Q3 - Flexibase - enviar para gestaodepessoas@flexibase.com.br até o final do dia útil seguinte'

export const ATA_OBJECTIVE = {
  intro: 'A Q3 é uma dinâmica de retrospectiva organizada em três perguntas:',
  items: [
    'QUE BOM: o que funcionou bem, gerou resultado, facilitou o trabalho ou merece ser reconhecido e mantido.',
    'QUE PENA: o que não funcionou como esperado, gerou dificuldades, retrabalho, atrasos, falhas de comunicação ou oportunidades de aprendizado.',
    'QUE TAL?: quais ideias, ajustes, experimentos ou ações podem melhorar a rotina, os processos e os resultados.',
  ],
} as const

export interface AtaInfoInput {
  readonly semana: string
  readonly areaName: string | null
  readonly data: string | null
  readonly inicio: string | null
  readonly fim: string | null
  readonly local: string | null
  readonly respReuniao: string | null
  readonly respAta: string | null
  readonly participantes: readonly AtaParticipanteInput[]
}

/** Convocado da ATA (pessoa do cadastro ou convidado) e a presença dele. */
export interface AtaParticipanteInput {
  readonly nome: string
  readonly presenca: AtaPresenca
}

/** Linha da lista de presença do PDF: Nº, participante, presença. */
export type AtaPresencaRow = readonly [string, string, string]

/** Lista de presença (Q3 e RAS), na ordem recebida, numerada 01, 02… */
export function presencaRows(participantes: readonly AtaParticipanteInput[]): AtaPresencaRow[] {
  return participantes.map((p, i) => [String(i + 1).padStart(2, '0'), p.nome, ATA_PRESENCA_LABEL[p.presenca]] as const)
}

export interface AtaTaskInput {
  readonly id: string
  readonly key: string
  readonly title: string
  readonly areaName: string
  readonly status: TaskStatus
  readonly prazo: string | null
  readonly executorName: string | null
  readonly responsavelName: string | null
  readonly q3_bom: string | null
  readonly q3_pena: string | null
}

export interface AtaDecisionInput {
  readonly decisao: string
  readonly ownerName: string | null
  readonly prazo: string | null
  readonly taskTitle: string | null
  readonly status: string
  /** Registro: sem responsável nem prazo — o PDF mostra "Registro" no lugar do responsável. */
  readonly tipo?: 'executar' | 'registro'
}

export interface AtaActionInput {
  readonly texto: string
  readonly taskTitle: string | null
  readonly quem: string | null
  readonly dueDate: string | null
  readonly dueTime: string | null
  readonly done: boolean
}

export interface AtaBullet {
  readonly title: string
  readonly area: string
  readonly text: string
}

export interface AtaModel {
  readonly title: 'ATA DE REUNIÃO Q3'
  readonly company: string
  /** Código do formulário e revisão ("R01 · Rev. 02"); ausente em snapshots antigos. */
  readonly docCode?: string
  /** Nome do sistema (rodapé de cada página); ausente em snapshots antigos. */
  readonly system?: string
  readonly weekLabel: string
  readonly info: readonly (readonly [string, string, string, string])[]
  /** Nomes dos convocados, separados por vírgula. */
  readonly participantes: string
  /** Lista de presença; ausente em snapshots fechados antes dela existir. */
  readonly presencas?: readonly AtaPresencaRow[]
  readonly objective: typeof ATA_OBJECTIVE
  readonly queBom: readonly AtaBullet[]
  readonly quePena: readonly AtaBullet[]
  readonly decisoes: readonly { readonly n: string; readonly decisao: string; readonly responsavel: string; readonly prazo: string }[]
  readonly prioridades: readonly string[]
  readonly planoAcao: readonly {
    readonly n: string
    readonly oQue: string
    readonly quem: string
    readonly prazo: string
    readonly status: string
  }[]
  readonly pendencias: readonly { readonly atividade: string; readonly area: string; readonly status: string; readonly prazo: string }[]
  readonly acompanhamento: readonly string[]
  readonly footer: string
  readonly isGeral: boolean
}

export const fmtDate = (iso: string | null | undefined): string =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : ''

const nn = (i: number) => String(i + 1).padStart(2, '0')

function prazoText(prazo: string | null, today: string): string {
  if (!prazo) return 'sem prazo'
  const d = daysBetween(today, prazo)
  const suffix = d < 0 ? ` (${-d} ${d === -1 ? 'dia atrasado' : 'dias atrasado'})` : d === 0 ? ' (vence hoje)' : ''
  return `prazo ${fmtDate(prazo)}${suffix}`
}

export function buildAtaModel(input: {
  readonly info: AtaInfoInput
  readonly tasks: readonly AtaTaskInput[]
  readonly decisions: readonly AtaDecisionInput[]
  readonly actions: readonly AtaActionInput[]
  readonly today: string
  /** Configurações → Modelos de ATA; sem elas, os padrões deste arquivo. */
  readonly texts?: { readonly company?: string; readonly footer?: string; readonly docCode?: string; readonly system?: string }
}): AtaModel {
  const { info, today } = input
  const texts = input.texts ?? {}
  const isGeral = info.areaName === null
  const tasks = [...input.tasks].sort((a, b) => a.areaName.localeCompare(b.areaName) || a.key.localeCompare(b.key))
  const withArea = (t: AtaTaskInput) => (isGeral ? ` (${t.areaName})` : '')

  const bullets = (field: 'q3_bom' | 'q3_pena'): AtaBullet[] =>
    tasks
      .filter((t) => (t[field] ?? '').trim())
      .map((t) => ({ title: t.title, area: t.areaName, text: (t[field] ?? '').trim() }))

  const abertas = tasks
    .filter((t) => isOpen(t.status))
    .sort((a, b) => (a.prazo ?? '9999').localeCompare(b.prazo ?? '9999'))

  const prioridades = abertas.map(
    (t, i) =>
      `${i + 1}. ${t.title}${withArea(t)} - ${TASK_STATUS_LABEL[t.status]}, ${prazoText(t.prazo, today)}` +
      (t.executorName || t.responsavelName ? ` - resp.: ${t.executorName ?? t.responsavelName}` : ''),
  )

  const actions = [...input.actions].sort((a, b) =>
    `${a.dueDate ?? '9999'}${a.dueTime ?? '99'}`.localeCompare(`${b.dueDate ?? '9999'}${b.dueTime ?? '99'}`),
  )

  const pendencias = abertas
    .filter((t) => t.prazo && daysBetween(today, t.prazo) <= 0)
    .map((t) => {
      const d = daysBetween(today, t.prazo as string)
      return {
        atividade: t.title,
        area: t.areaName,
        status: TASK_STATUS_LABEL[t.status],
        prazo: `${fmtDate(t.prazo)}\n${d < 0 ? `${-d} ${d === -1 ? 'dia atrasado' : 'dias atrasado'}` : 'vence hoje'}`,
      }
    })

  const decisoesValidas = input.decisions.filter((d) => d.decisao.trim())
  const acompanhamento = [
    ...actions
      .filter((a) => !a.done)
      .map((a) => ({
        k: a.dueDate ?? '9999',
        tx:
          `Ação: ${a.texto}` +
          (a.taskTitle ? ` (Atividade: ${a.taskTitle})` : '') +
          (a.dueDate ? ` - até ${fmtDate(a.dueDate)}` : '') +
          (a.dueDate && a.dueDate < today ? ' - ATRASADA' : ''),
      })),
    // Registro não é pendência: só documenta o que foi decidido.
    ...decisoesValidas
      .filter((d) => d.status === 'aberta' && d.tipo !== 'registro')
      .map((d) => ({
        k: d.prazo ?? '9999',
        tx:
          `Decisão Q3: ${d.decisao.trim()}` +
          (d.ownerName ? ` - resp.: ${d.ownerName}` : '') +
          (d.prazo ? ` - até ${fmtDate(d.prazo)}` : ''),
      })),
  ]
    .sort((a, b) => a.k.localeCompare(b.k))
    .map((x) => x.tx)

  return {
    title: 'ATA DE REUNIÃO Q3',
    company: texts.company || 'FLEXIBASE',
    docCode: texts.docCode ?? 'R01',
    system: texts.system ?? '',
    weekLabel: weekLabel(info.semana),
    isGeral,
    info: [
      ['Data', fmtDate(info.data), 'Horário de início', info.inicio ?? ''],
      ['Horário de término', info.fim ?? '', 'Local / Plataforma', info.local ?? ''],
      ['Área', info.areaName ?? 'GERAL (todas as áreas)', 'Responsável pela reunião', info.respReuniao ?? ''],
      ['Responsável pela ata', info.respAta ?? '', 'Semana', weekLabel(info.semana).replace(/^Semana /, '')],
    ],
    participantes: info.participantes.map((p) => p.nome).join(', '),
    presencas: presencaRows(info.participantes),
    objective: ATA_OBJECTIVE,
    queBom: bullets('q3_bom'),
    quePena: bullets('q3_pena'),
    decisoes: decisoesValidas.map((d, i) => ({
      n: nn(i),
      decisao: d.decisao.trim() + (d.taskTitle ? `\n(Atividade: ${d.taskTitle})` : ''),
      responsavel: d.tipo === 'registro' ? 'Registro' : (d.ownerName ?? ''),
      prazo: d.tipo === 'registro' ? '' : fmtDate(d.prazo),
    })),
    prioridades,
    planoAcao: actions.map((a, i) => ({
      n: nn(i),
      oQue: a.texto + (a.taskTitle ? `\n(Atividade: ${a.taskTitle})` : ''),
      quem: a.quem ?? '',
      prazo: a.dueDate ? fmtDate(a.dueDate) + (a.dueTime ? ` ${a.dueTime.slice(0, 5)}` : '') : '',
      status: a.done ? 'Concluída' : a.dueDate && a.dueDate < today ? 'Atrasada' : 'Em aberto',
    })),
    pendencias,
    acompanhamento,
    footer: texts.footer ?? ATA_FOOTER,
  }
}
